
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCollectionsFetcher } from "./useCollectionsFetcher";
import { useContentAutoSave } from "./useContentAutoSave";
import { VALID_CONTENT_TYPES, ContentType } from "@/utils/contentFormUtils";
import { toast } from "sonner";

export const useContentDetailPanel = (contentItem: any, onUpdate: () => void) => {
  const { user } = useAuth();
  const [url, setUrl] = useState(contentItem.url || "");
  
  // Initialize content type from the contentItem prop, ensuring it's lowercase
  const initialContentType = contentItem.content_type ? 
    contentItem.content_type.toLowerCase() : "webpage";
  const [contentType, setContentType] = useState(initialContentType);
  
  const [collectionId, setCollectionId] = useState(contentItem.collection_id || "");
  const [notes, setNotes] = useState(contentItem.notes || "");
  
  const contentItemIdRef = useRef<string>(contentItem.id);

  const { collections } = useCollectionsFetcher(user);
  const { isSaving, debouncedSave } = useContentAutoSave(contentItem.id, user, onUpdate);

  // Debug log initial content type
  useEffect(() => {
    console.log(`[DEBUG] Initial content type for item ${contentItem.id}:`, {
      raw: contentItem.content_type,
      normalized: contentItem.content_type ? contentItem.content_type.toLowerCase() : null,
      stateValue: contentType,
      validTypes: VALID_CONTENT_TYPES
    });
  }, [contentItem.id, contentItem.content_type, contentType]);

  // Update local state when contentItem changes
  useEffect(() => {
    if (contentItemIdRef.current !== contentItem.id) {
      console.log(`[DEBUG] Content item ID changed from ${contentItemIdRef.current} to ${contentItem.id}, updating states`);
      contentItemIdRef.current = contentItem.id;
      
      setUrl(contentItem.url || "");
      const normalizedType = contentItem.content_type ? contentItem.content_type.toLowerCase() : "webpage";
      setContentType(normalizedType);
      setCollectionId(contentItem.collection_id || "");
      setNotes(contentItem.notes || "");
      
      console.log(`[DEBUG] States reset for new content item:`, {
        url: contentItem.url || "",
        contentType: normalizedType,
        collectionId: contentItem.collection_id || "",
        notes: contentItem.notes || ""
      });
    }
  }, [contentItem.id, contentItem.url, contentItem.collection_id, contentItem.notes, contentItem.content_type]);

  const handleUrlChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    return await debouncedSave({ url: newUrl });
  };

  const handleContentTypeChange = async (newType: string) => {
    console.log(`[DEBUG] handleContentTypeChange called with: ${newType}`);
    
    const normalizedType = newType.toLowerCase();
    
    if (!VALID_CONTENT_TYPES.includes(normalizedType as ContentType)) {
      console.error(`[DEBUG] Invalid content type selected: ${normalizedType}`);
      toast.error(`Invalid content type: ${normalizedType}`);
      return false;
    }
    
    // Update local state immediately
    setContentType(normalizedType);
    
    // Save to server - cast to ContentType since we've validated it above
    console.log(`[DEBUG] Saving normalized content type: ${normalizedType}`);
    return await debouncedSave({ content_type: normalizedType as ContentType });
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newNotes = e.target.value;
    setNotes(newNotes);
    // Don't auto-save notes anymore - only update local state
  };

  // New function to handle explicit notes saving
  const handleNotesSave = async (): Promise<boolean> => {
    console.log(`[DEBUG] Explicitly saving notes: ${notes}`);
    return await debouncedSave({ notes });
  };

  const handleCollectionChange = async (newCollectionId: string) => {
    setCollectionId(newCollectionId);
    return await debouncedSave({ collection_id: newCollectionId });
  };

  const tags = [
    { id: "tag1", name: "Tag 1" },
    { id: "tag2", name: "Tag 2" },
    { id: "tag3", name: "Tagged Three" },
    { id: "tag4", name: "Tag 4" },
  ];

  return {
    url,
    contentType,
    collectionId,
    notes,
    isSaving,
    collections,
    tags,
    handleUrlChange,
    handleContentTypeChange,
    handleNotesChange,
    handleNotesSave,
    handleCollectionChange,
    contentItemId: contentItem.id
  };
};
